import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, Send, Settings, Bell, CheckCircle, 
  AlertTriangle, DollarSign, TrendingUp, Clock, Zap,
  Hash, Bot, Webhook, TestTube, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CostBot = () => {
  const { shouldShowData } = useDemoData();

  const [slackConfig, setSlackConfig] = useState({
    enabled: false,
    webhookUrl: '',
    channel: '#cost-alerts',
    dailyDigest: true,
    digestTime: '09:00',
    alertOnAnomaly: true,
    alertOnBudgetWarning: true,
    alertOnBudgetExceeded: true,
    alertOnSavingsFound: true
  });

  const [teamsConfig, setTeamsConfig] = useState({
    enabled: false,
    webhookUrl: '',
    dailyDigest: true,
    digestTime: '09:00',
    alertOnAnomaly: true,
    alertOnBudgetWarning: true
  });

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/integrations/messaging`);
      // Merge server values OVER defaults so controlled inputs never receive undefined.
      if (response.data?.slack) setSlackConfig((prev) => ({ ...prev, ...response.data.slack, webhookUrl: response.data.slack.webhookUrl ?? '', channel: response.data.slack.channel ?? '#cost-alerts' }));
      if (response.data?.teams) setTeamsConfig((prev) => ({ ...prev, ...response.data.teams, webhookUrl: response.data.teams.webhookUrl ?? '' }));
    } catch (error) {
      // Use defaults if not configured
    }
  };

  const saveConfig = async (platform) => {
    setSaving(true);
    try {
      const config = platform === 'slack' ? slackConfig : teamsConfig;
      await axios.post(`${API_URL}/integrations/messaging/${platform}`, config);
      toast.success(`${platform === 'slack' ? 'Slack' : 'Teams'} configuration saved`);
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (platform) => {
    setTesting(true);
    try {
      const config = platform === 'slack' ? slackConfig : teamsConfig;
      await axios.post(`${API_URL}/integrations/messaging/${platform}/test`, {
        webhookUrl: config.webhookUrl
      });
      toast.success(`Test message sent to ${platform === 'slack' ? 'Slack' : 'Teams'}!`);
    } catch (error) {
      toast.error('Failed to send test message. Check your webhook URL.');
    } finally {
      setTesting(false);
    }
  };

  const sendDailySummary = async (platform) => {
    setTesting(true);
    try {
      await axios.post(`${API_URL}/integrations/messaging/${platform}/daily-summary`);
      toast.success('Daily cost summary sent!');
    } catch (error) {
      toast.error('Failed to send summary');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const alertTypes = [
    { key: 'alertOnAnomaly', icon: AlertTriangle, label: 'Cost Anomalies', desc: 'Unusual spending detected', color: 'text-amber-500' },
    { key: 'alertOnBudgetWarning', icon: Bell, label: 'Budget Warnings', desc: '80% of budget consumed', color: 'text-orange-500' },
    { key: 'alertOnBudgetExceeded', icon: DollarSign, label: 'Budget Exceeded', desc: 'Budget limit reached', color: 'text-rose-500' },
    { key: 'alertOnSavingsFound', icon: TrendingUp, label: 'Savings Opportunities', desc: 'New optimization found', color: 'text-emerald-500' }
  ];

  if (!shouldShowData) {
    return <EmptyDataState title="Cost Bot" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="p-6 space-y-6" data-testid="cost-bot-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-3">
            <Bot className="w-7 h-7 text-emerald-500" />
            Cost Bot
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            Get cost alerts and daily digests in Slack or Microsoft Teams
          </p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <Zap className="w-3 h-3 mr-1" />
          Real-time Alerts
        </Badge>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="slack" className="space-y-6">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1">
          <TabsTrigger value="slack" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Hash className="w-4 h-4" />
            Slack
          </TabsTrigger>
          <TabsTrigger value="teams" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <MessageSquare className="w-4 h-4" />
            Microsoft Teams
          </TabsTrigger>
        </TabsList>

        {/* Slack Tab */}
        <TabsContent value="slack" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Slack Configuration
                </CardTitle>
                <CardDescription>
                  Connect CARTIEAI to your Slack workspace
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${slackConfig.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Enable Slack Bot</p>
                      <p className="text-sm text-slate-400">Send alerts to Slack</p>
                    </div>
                  </div>
                  <Switch
                    checked={slackConfig.enabled}
                    onCheckedChange={(checked) => setSlackConfig({ ...slackConfig, enabled: checked })}
                    data-testid="slack-enabled-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slack-webhook">Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="slack-webhook"
                      type="password"
                      placeholder="https://hooks.slack.com/services/..."
                      value={slackConfig.webhookUrl}
                      onChange={(e) => setSlackConfig({ ...slackConfig, webhookUrl: e.target.value })}
                      className="font-mono text-sm"
                      data-testid="slack-webhook-input"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open('https://api.slack.com/messaging/webhooks', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">
                    Create a webhook at api.slack.com/messaging/webhooks
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slack-channel">Channel</Label>
                  <Input
                    id="slack-channel"
                    placeholder="#cost-alerts"
                    value={slackConfig.channel}
                    onChange={(e) => setSlackConfig({ ...slackConfig, channel: e.target.value })}
                    data-testid="slack-channel-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => testConnection('slack')}
                    disabled={!slackConfig.webhookUrl || testing}
                    variant="outline"
                    className="gap-2"
                    data-testid="slack-test-btn"
                  >
                    <TestTube className="w-4 h-4" />
                    Test Connection
                  </Button>
                  <Button
                    onClick={() => saveConfig('slack')}
                    disabled={saving}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="slack-save-btn"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Save Config
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Alert Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Alert Settings
                </CardTitle>
                <CardDescription>
                  Choose which alerts to receive in Slack
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {alertTypes.map((alert) => (
                  <div
                    key={alert.key}
                    className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-emerald-500/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <alert.icon className={`w-5 h-5 ${alert.color}`} />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{alert.label}</p>
                        <p className="text-xs text-slate-400">{alert.desc}</p>
                      </div>
                    </div>
                    <Switch
                      checked={slackConfig[alert.key]}
                      onCheckedChange={(checked) => setSlackConfig({ ...slackConfig, [alert.key]: checked })}
                    />
                  </div>
                ))}

                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Daily Digest</p>
                      <p className="text-sm text-slate-400">Receive a daily cost summary</p>
                    </div>
                    <Switch
                      checked={slackConfig.dailyDigest}
                      onCheckedChange={(checked) => setSlackConfig({ ...slackConfig, dailyDigest: checked })}
                    />
                  </div>
                  {slackConfig.dailyDigest && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <Input
                        type="time"
                        value={slackConfig.digestTime}
                        onChange={(e) => setSlackConfig({ ...slackConfig, digestTime: e.target.value })}
                        className="w-32"
                      />
                      <span className="text-sm text-slate-400">UTC</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Quick Actions
              </CardTitle>
              <CardDescription>
                Test different message types
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => sendDailySummary('slack')}
                  disabled={!slackConfig.webhookUrl || testing}
                  variant="outline"
                  className="gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send Daily Summary
                </Button>
                <Button
                  onClick={() => testConnection('slack')}
                  disabled={!slackConfig.webhookUrl || testing}
                  variant="outline"
                  className="gap-2"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Send Test Anomaly Alert
                </Button>
                <Button
                  onClick={() => testConnection('slack')}
                  disabled={!slackConfig.webhookUrl || testing}
                  variant="outline"
                  className="gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Send Budget Alert
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Microsoft Teams Configuration
                </CardTitle>
                <CardDescription>
                  Connect CARTIEAI to Microsoft Teams
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${teamsConfig.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Enable Teams Bot</p>
                      <p className="text-sm text-slate-400">Send alerts to Teams</p>
                    </div>
                  </div>
                  <Switch
                    checked={teamsConfig.enabled}
                    onCheckedChange={(checked) => setTeamsConfig({ ...teamsConfig, enabled: checked })}
                    data-testid="teams-enabled-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="teams-webhook">Incoming Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="teams-webhook"
                      type="password"
                      placeholder="https://outlook.office.com/webhook/..."
                      value={teamsConfig.webhookUrl}
                      onChange={(e) => setTeamsConfig({ ...teamsConfig, webhookUrl: e.target.value })}
                      className="font-mono text-sm"
                      data-testid="teams-webhook-input"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open('https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => testConnection('teams')}
                    disabled={!teamsConfig.webhookUrl || testing}
                    variant="outline"
                    className="gap-2"
                    data-testid="teams-test-btn"
                  >
                    <TestTube className="w-4 h-4" />
                    Test Connection
                  </Button>
                  <Button
                    onClick={() => saveConfig('teams')}
                    disabled={saving}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="teams-save-btn"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Save Config
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Alert Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {alertTypes.slice(0, 2).map((alert) => (
                  <div
                    key={alert.key}
                    className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <alert.icon className={`w-5 h-5 ${alert.color}`} />
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{alert.label}</p>
                        <p className="text-xs text-slate-400">{alert.desc}</p>
                      </div>
                    </div>
                    <Switch
                      checked={teamsConfig[alert.key]}
                      onCheckedChange={(checked) => setTeamsConfig({ ...teamsConfig, [alert.key]: checked })}
                    />
                  </div>
                ))}

                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Daily Digest</p>
                      <p className="text-sm text-slate-400">Receive a daily cost summary</p>
                    </div>
                    <Switch
                      checked={teamsConfig.dailyDigest}
                      onCheckedChange={(checked) => setTeamsConfig({ ...teamsConfig, dailyDigest: checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Example Messages */}
      <Card>
        <CardHeader>
          <CardTitle>Message Preview</CardTitle>
          <CardDescription>Example of what messages look like</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-xl p-4 text-white font-mono text-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded bg-emerald-600 flex items-center justify-center shrink-0">
                <Bot className="w-5 h-5" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                  <span className="text-slate-400 text-xs">APP 9:00 AM</span>
                </div>
                <div className="bg-slate-800 rounded-lg p-3 border-l-4 border-emerald-500">
                  <p className="font-semibold text-emerald-400 mb-2">Daily Cost Summary</p>
                  <p className="text-slate-300">Total Spend Today: <span className="text-white font-bold">$2,847</span></p>
                  <p className="text-slate-300">vs Yesterday: <span className="text-rose-400">+12%</span></p>
                  <p className="text-slate-300">Active Anomalies: <span className="text-amber-400">2</span></p>
                  <p className="text-slate-300">Potential Savings: <span className="text-emerald-400">$1,034</span></p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostBot;
